import ForumIcon from '@mui/icons-material/Forum'
import {
  Badge,
  Box,
  Button,
  Link,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { TABLET_WIDTH_BREAKPOINT } from '../../config/breakPoints'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { FeatureFlags } from '../../models/FeatureFlags'
import {
  NotificationContext,
  TwilioConversationsContext,
  WindowSizeContext
} from '../../providers'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { RepairOrderContext } from '../../providers/RepairOrderProvider/RepairOrderProvider'
import { useUpdateVehicleDetailMutation } from '../../redux/api'
import { describeVehicle } from '../../utils'
import { BrowseFluidsTaxonomyButton } from '../BrowseFluidsTaxonomy/BrowseFluidsTaxonomyButton'
import { CarFaxHistoryButton } from '../CarFaxHistory/CarFaxHistoryButton'
import { FutureDatePicker } from '../SharedUI/FutureDatePicker'
import { MilesInMilesOutText } from '../Vehicles/MilesInMilesOutText'
import LaborGuideButton from './LaborGuideButton'
import { MaintenanceScheduleButton } from './MaintenanceSchedules/MaintenanceScheduleButton'

interface Props {
  repairOrderId: string
  setCustomerChatIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

// eslint-disable-next-line complexity
const RelatedPanel: FC<Props> = ({ setCustomerChatIsOpen }) => {
  const { featureFlags } = useContext(AppContext)
  const { accessToken } = useAccessTokenAndShopId()
  const { setNotice } = useContext(NotificationContext)
  const { repairOrder, customer, vehicle } = useContext(RepairOrderContext)
  const { width } = useContext(WindowSizeContext)
  const overTabletBreakpoint = width >= TABLET_WIDTH_BREAKPOINT
  const { conversationUniqueNamesWithUnreadMessages } = useContext(
    TwilioConversationsContext
  )

  const [updateVehicle] = useUpdateVehicleDetailMutation()

  const [nextInspectionDate, setNextInspectionDate] = useState<Dayjs | null>(
    dayjs(vehicle?.info?.nextInspectionDate || null)
  )
  useEffect(() => {
    setNextInspectionDate(dayjs(vehicle?.info?.nextInspectionDate || null))
  }, [vehicle?.info?.nextInspectionDate])

  const [nextOilServiceDate, setNextOilServiceDate] = useState<Dayjs | null>(
    dayjs(vehicle?.info?.nextOilServiceDate || null)
  )
  useEffect(() => {
    setNextOilServiceDate(dayjs(vehicle?.info?.nextOilServiceDate || null))
  }, [vehicle?.info?.nextOilServiceDate])

  const [nextOilServiceMileage, setNextOilServiceMileage] = useState<
    number | undefined
  >(vehicle?.info?.nextOilServiceMileage)
  useEffect(() => {
    vehicle?.info?.nextOilServiceMileage &&
      setNextOilServiceMileage(vehicle?.info?.nextOilServiceMileage)
  }, [vehicle?.info?.nextOilServiceMileage])

  const handleUpdateDate = useCallback(
    async (
      field: 'nextInspectionDate' | 'nextOilServiceDate',
      newDate: Dayjs | null
    ) => {
      const fieldName =
        field === 'nextInspectionDate'
          ? 'Next Inspection Date'
          : 'Next Oil Service Date'

      if (newDate && newDate.toDate() <= new Date()) {
        return
      }

      await updateVehicle({
        authorization: accessToken,
        vehicleEntry: {
          ...(vehicle && {
            ...vehicle,
            info: {
              ...vehicle?.info,
              [field]: newDate?.isValid()
                ? newDate?.startOf('day').toDate()
                : undefined
            }
          })
        }
      })

      if (newDate?.isValid()) {
        setNotice(`${fieldName} updated`)
      } else {
        setNotice(`${fieldName} cleared`)
      }
    },
    [accessToken, setNotice, updateVehicle, vehicle]
  )

  const handleUpdateNextOilServiceMileage = useCallback(async () => {
    await updateVehicle({
      authorization: accessToken,
      vehicleEntry: {
        ...(vehicle && {
          ...vehicle,
          info: {
            ...vehicle?.info,
            nextOilServiceMileage
          }
        })
      }
    })

    setNotice(`nextOilServiceMileage updated`)
  }, [accessToken, nextOilServiceMileage, setNotice, updateVehicle, vehicle])

  return (
    <Paper variant="outlined" data-testid="RelatedPanel">
      <Stack p={2} spacing={1}>
        {vehicle && (
          <>
            <Link
              component={RouterLink}
              to={AppRoutes.VEHICLES + '/' + vehicle.id!}>
              <Typography variant="h3">
                {describeVehicle(vehicle.info!)}
              </Typography>
            </Link>
            <Box>
              <BrowseFluidsTaxonomyButton vehicle={vehicle} />
              <MaintenanceScheduleButton />
              {featureFlags![FeatureFlags.TruSpeed] && (
                <LaborGuideButton vehicle={vehicle} />
              )}
              <CarFaxHistoryButton vehicle={vehicle} />
            </Box>
            {vehicle.info!.subModel?.name ? (
              <Typography>{`Submodel: ${
                vehicle.info!.subModel?.name
              }`}</Typography>
            ) : (
              <Typography>{'Submodel: N/A'}</Typography>
            )}
            {vehicle.info!.engine?.name ? (
              <Typography>{`Engine: ${vehicle
                .info!.engine?.name.toLowerCase()
                .replace(/(^\w{1})|(\s+\w{1})/g, (letter) =>
                  letter.toUpperCase()
                )}`}</Typography>
            ) : (
              <Typography>{'Engine: N/A'}</Typography>
            )}
            {vehicle.info!.licensePlate ? (
              vehicle.info!.registeredState ? (
                <Typography>{`${vehicle.info!.licensePlate} - ${
                  vehicle.info!.registeredState
                }`}</Typography>
              ) : (
                <Typography>{vehicle.info!.licensePlate}</Typography>
              )
            ) : (
              <Typography>{'License Plate: N/A'}</Typography>
            )}
            {vehicle.info!.vin ? (
              <Typography>{`VIN: ${vehicle.info!.vin}`}</Typography>
            ) : (
              <Typography>{'VIN: N/A'}</Typography>
            )}
            {repairOrder && (
              <MilesInMilesOutText
                repairOrder={repairOrder}
                vehicle={vehicle}
                stackProps={{
                  direction: 'row',
                  justifyContent: 'space-between'
                }}
                textFieldProps={{
                  size: 'small',
                  sx: {
                    width: '15ch'
                  }
                }}
              />
            )}

            <FutureDatePicker
              label={'Next Inspection'}
              value={
                nextInspectionDate?.toString() !== 'Invalid Date'
                  ? nextInspectionDate
                  : null
              }
              onChange={(newDate) => {
                setNextInspectionDate(newDate)
                handleUpdateDate('nextInspectionDate', newDate)
              }}
              canClear={true}
              variant={'standard'}
              sx={{ flexGrow: 1 }}
            />

            <Stack direction="row" justifyContent="space-between">
              <TextField
                size="small"
                variant="standard"
                type="number"
                value={nextOilServiceMileage}
                sx={{
                  width: '15ch'
                }}
                label={'Next Oil Service Mileage'}
                onChange={(event) =>
                  setNextOilServiceMileage(+event.target.value)
                }
                onBlur={handleUpdateNextOilServiceMileage}
              />

              <FutureDatePicker
                label={'Next Oil Service'}
                value={
                  nextOilServiceDate?.toString() !== 'Invalid Date'
                    ? nextOilServiceDate
                    : null
                }
                onChange={(newDate) => {
                  setNextOilServiceDate(newDate)
                  handleUpdateDate('nextOilServiceDate', newDate)
                }}
                canClear={true}
                variant={'standard'}
                sx={{
                  width: '15ch'
                }}
              />
            </Stack>
          </>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={'center'}
          spacing={2}>
          {!overTabletBreakpoint && (
            <Badge
              color="error"
              variant="dot"
              invisible={
                !conversationUniqueNamesWithUnreadMessages.includes(
                  customer?.id || ''
                )
              }>
              <Button
                data-testid="smsButton"
                variant="contained"
                startIcon={<ForumIcon />}
                onClick={() => setCustomerChatIsOpen(true)}>
                SMS
              </Button>
            </Badge>
          )}
          {/* <AppointmentSection /> */}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default RelatedPanel
